import isEmpty from 'lodash/isEmpty'
import moment from 'moment-timezone'

import {
  ForecastUpdatedVia,
  ShippingType,
  MaterialDemandByDay,
  MaterialDemandById,
  MaterialDemandEntry,
  DemandDataPerMaterial,
  DemandForecastPerType,
  AggregatedDemandData,
  ExtendedLastUpdate
} from '../types'

/**
 * Helper function to filter demand daily entries by materialIds
 * @param materialDemandById - Demand entries indexed by material ID
 * @param materialIds - Optional list of material IDs to filter by
 * @returns list of demand entries for selected materials
 */
export const getSelectedMaterialDemandEntries = (
  materialDemandById: MaterialDemandById,
  materialIds?: number[]
) => {
  if (materialIds === undefined) {
    return Object.values(materialDemandById)
  }
  return Object.entries(materialDemandById)
    .filter(([id]) => {
      return materialIds.includes(Number(id))
    })
    .map(([, demand]) => demand)
}

/**
 * Calculate total value across all shipping types
 * @param demandPerType - Demand values per shipping type
 * @returns Total sum or null if empty
 */
export const getTotalValueForShippingTypes = (demandPerType: Partial<DemandForecastPerType>) => {
  return isEmpty(demandPerType)
    ? null
    : Object.values(demandPerType).reduce((acc, value) => acc + value, 0)
}

const getUpdatedVia = (
  userValue: number | undefined,
  mailValue: number | undefined
): ForecastUpdatedVia | undefined => {
  // user overrides have priority, if there are user and mail overrides,
  // BE should return last updated data for user
  if (userValue !== undefined) {
    return ForecastUpdatedVia.User
  }
  if (mailValue !== undefined) {
    return ForecastUpdatedVia.Mail
  }
  return undefined
}

/**
 * Merge last updated fields from multiple entries, keeping the most recent
 * @param acc - Accumulator for last updates
 * @param current - Current entry with override and update info
 */
export function mergeUpdatedFields(
  acc: AggregatedDemandData['lastUpdates'],
  current: Pick<MaterialDemandEntry, 'userOverrides' | 'schedules' | 'lastUpdates'>
) {
  Object.entries(current.lastUpdates).forEach(([shippingType, currentLastUpdate]) => {
    const type = shippingType as ShippingType
    const existingLastUpdate = acc[type]

    const userValue = current.userOverrides[type]
    const mailValue = current.schedules[type]
    const updatedVia = getUpdatedVia(userValue, mailValue)
    if (!updatedVia) {
      throw new Error(
        'BUG: if there are overrides for a shipping type, there should be an updatedVia'
      )
    }
    const lastUpdateData: ExtendedLastUpdate = {...currentLastUpdate, updatedVia}

    if (!existingLastUpdate) {
      acc[shippingType] = lastUpdateData
      return
    }
    if (moment.utc(existingLastUpdate.updatedOn).isBefore(currentLastUpdate.updatedOn)) {
      acc[shippingType] = lastUpdateData
    }
  })
}

/**
 * Function used for demand table - aggregates demand data for a range or single date
 * @param materialDemandByDay - Demand data indexed by day
 * @returns Demand data aggregated per material
 */
export const getDemandDataPerMaterial = (
  materialDemandByDay: MaterialDemandByDay
): DemandDataPerMaterial => {
  const demandEntriesByMaterialId = Object.values(materialDemandByDay).reduce<
    Record<string, MaterialDemandEntry[]>
  >((acc, materialDemandById) => {
    Object.entries(materialDemandById).forEach(([materialId, demand]) => {
      if (!acc[materialId]) {
        acc[materialId] = []
      }
      acc[materialId].push(demand)
    })
    return acc
  }, {})

  const aggregatedDemandByMaterialId = Object.fromEntries(
    Object.entries(demandEntriesByMaterialId).map(([materialId, entries]) => {
      return [materialId, getAggregatedDemandData(entries)]
    })
  )

  return aggregatedDemandByMaterialId
}

/**
 * Increment demand values by shipping type
 * @param acc - Accumulator for demand values
 * @param values - Values to add
 */
export const incrementDemandValues = (
  acc: Partial<DemandForecastPerType>,
  values: Partial<DemandForecastPerType>
) => {
  Object.entries(values).forEach(([shippingType, value]) => {
    acc[shippingType] = (acc[shippingType] ?? 0) + value
  })
}

/**
 * Aggregate demand data from multiple entries
 * @param materialDemand - Array of demand entries to aggregate
 * @returns Aggregated demand data
 */
export const getAggregatedDemandData = (materialDemand: MaterialDemandEntry[]) => {
  return materialDemand.reduce<AggregatedDemandData>(
    (acc, entry) => {
      incrementDemandValues(acc.actuals, entry.actuals)
      incrementDemandValues(acc.preOrders, entry.preOrders)
      incrementDemandValues(acc.merged, entry.merged)
      incrementDemandValues(acc.predictions, entry.predictions)
      incrementDemandValues(acc.salesForecast, entry.salesForecast)
      incrementDemandValues(acc.userOverrides, entry.userOverrides)
      incrementDemandValues(acc.schedules, entry.schedules)
      mergeUpdatedFields(acc.lastUpdates, entry)
      return acc
    },
    {
      actuals: {},
      preOrders: {},
      merged: {},
      userOverrides: {},
      schedules: {},
      predictions: {},
      salesForecast: {},
      lastUpdates: {}
    }
  )
}
